import React, { useState, useEffect } from 'react';
import { FiExternalLink, FiCalendar, FiUser, FiBookOpen, FiTrendingUp, FiLink, FiArrowDown, FiArrowUp, FiCornerDownRight, FiSave, FiChevronDown, FiChevronUp, FiUpload, FiFile, FiDownload, FiTrash2 } from 'react-icons/fi';
import { getApiEndpoint } from '../utils/api';
import './SearchResults.css';

const SearchResults = ({ results, currentPage, onPageChange, perPage, searchQuery = '', onCitationSearch, onSaveToCollection, isAuthenticated, isCollectionView = false, collectionId = null, onPdfUpload, relevanceThreshold, onRemoveFromCollection, onSignInToSave }) => {
  const { results: works = [], meta = {} } = results;
  const { count = 0, per_page = 25 } = meta;
  const hasRelevanceFilter = relevanceThreshold !== undefined && relevanceThreshold !== null && relevanceThreshold !== '';
  // Use currentPage prop if available, otherwise fall back to meta.page, default to 1
  const totalPages = Math.ceil(count / per_page);
  let page = currentPage || meta.page || 1;
  // Ensure page doesn't exceed total pages
  if (totalPages > 0 && page > totalPages) {
    page = totalPages;
  }
  const [expandedAbstracts, setExpandedAbstracts] = useState(new Set());
  const [pdfStatus, setPdfStatus] = useState({}); // { workId: { hasPdf: bool, uploading: bool, filename: string } }

  const formatRelevance = (value) => {
    if (typeof value !== 'number') return 'N/A';
    return value.toFixed(2);
  };

  const relevanceScores = works
    .map(work => (typeof work?.relevance_score === 'number' ? work.relevance_score : null))
    .filter(score => score !== null);

  const maxRelevance = relevanceScores.length ? Math.max(...relevanceScores) : null;
  const minRelevance = relevanceScores.length ? Math.min(...relevanceScores) : null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatAuthors = (authorships) => {
    if (!authorships || authorships.length === 0) return 'Unknown authors';
    const authorNames = authorships.slice(0, 3).map(a => {
      const name = a.author?.display_name || 'Unknown';
      return removeOpenAlexUrls(name);
    });
    if (authorships.length > 3) {
      return `${authorNames.join(', ')}, and ${authorships.length - 3} more`;
    }
    return authorNames.join(', ');
  };

  const getOpenAlexId = (id) => {
    if (!id) return '';
    return id.replace('https://openalex.org/', '');
  };

  const getWorkType = (type) => {
    if (!type) return 'Work';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Remove OpenAlex URLs from text
  const removeOpenAlexUrls = (text) => {
    if (!text) return text;
    // Remove URLs like https://openalex.org/P4310320990 or https://openalex.org/W123456789
    // Also handle URLs with trailing slashes, spaces, or other characters
    let cleaned = text.replace(/https?:\/\/openalex\.org\/[A-Z]\d+\/?/gi, '');
    // Remove any extra spaces or punctuation left behind
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    // Remove trailing commas, periods, or other punctuation that might be left
    cleaned = cleaned.replace(/^[,\s\.]+|[,\s\.]+$/g, '');
    return cleaned;
  };

  // Highlight search terms in text - DISABLED for now
  const highlightText = (text) => {
    if (!text) return text;
    // Just remove OpenAlex URLs and return plain text
    return removeOpenAlexUrls(text);
  };

  const formatCitationCount = (count) => {
    if (!count && count !== 0) return null;
    return count.toLocaleString();
  };

  // Check PDF status for all works when in collection view
  useEffect(() => {
    if (isCollectionView && collectionId && works.length > 0) {
      works.forEach(async (work) => {
        const workId = getOpenAlexId(work.id);
        if (!workId) return;
        
        try {
          const { getAuthHeaders } = await import('../utils/auth');
          const response = await fetch(
            getApiEndpoint(`/api/collections/${collectionId}/works/${workId}/pdf`),
            { headers: getAuthHeaders() }
          );
          if (response.ok) {
            const data = await response.json();
            setPdfStatus(prev => ({
              ...prev,
              [work.id]: { 
                hasPdf: data.has_pdf, 
                uploading: false,
                filename: data.filename || null
              }
            }));
          }
        } catch (err) {
          // Ignore errors
        }
      });
    }
  }, [isCollectionView, collectionId, works]);

  const handleFileSelect = async (work, event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.includes('pdf')) {
      alert('Please select a PDF file');
      return;
    }

    const workId = getOpenAlexId(work.id);
    if (!workId || !collectionId) return;

    // Set uploading state
    setPdfStatus(prev => ({
      ...prev,
      [work.id]: { ...prev[work.id], uploading: true }
    }));

    try {
      const { getAuthHeaders } = await import('../utils/auth');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        getApiEndpoint(`/api/collections/${collectionId}/works/${workId}/upload-pdf`),
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: formData
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload PDF');
      }

      const result = await response.json();
      // Update status
      setPdfStatus(prev => ({
        ...prev,
        [work.id]: { 
          hasPdf: true, 
          uploading: false,
          filename: result.filename || file.name
        }
      }));

      if (onPdfUpload) {
        onPdfUpload(work, file.name);
      }
    } catch (err) {
      alert(`Failed to upload PDF: ${err.message}`);
      setPdfStatus(prev => ({
        ...prev,
        [work.id]: { ...prev[work.id], uploading: false }
      }));
    }

    // Reset file input
    event.target.value = '';
  };

  const handleDownloadPdf = async (work) => {
    const workId = getOpenAlexId(work.id);
    if (!workId || !collectionId) return;

    try {
      const { getAuthHeaders } = await import('../utils/auth');
      const response = await fetch(
        getApiEndpoint(`/api/collections/${collectionId}/works/${workId}/pdf/download`),
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfStatus[work.id]?.filename || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(`Failed to download PDF: ${err.message}`);
    }
  };

  return (
    <div className="search-results">
      <div className="results-header">
        <h2>
          {count.toLocaleString()} {count === 1 ? 'result' : 'results'} found
        </h2>
        {count > 0 && (
          <p className="results-info">
            {hasRelevanceFilter ? (
              meta.filtered_count ? (
                <>
                  Showing {meta.filtered_count.toLocaleString()} on this page after relevance ≥ {relevanceThreshold}. Original range {((page - 1) * per_page + 1).toLocaleString()} - {Math.min(page * per_page, count).toLocaleString()} of {count.toLocaleString()}.
                </>
              ) : (
                <>
                  No results on this page after relevance ≥ {relevanceThreshold}. Original range {((page - 1) * per_page + 1).toLocaleString()} - {Math.min(page * per_page, count).toLocaleString()} of {count.toLocaleString()}.
                </>
              )
            ) : (
              <>
                Showing {((page - 1) * per_page + 1).toLocaleString()} - {Math.min(page * per_page, count).toLocaleString()} of {count.toLocaleString()}
              </>
            )}
          </p>
        )}
        {(minRelevance !== null || maxRelevance !== null) && (
          <p className="results-info">
            Relevance range (this page):
            {' '}min {formatRelevance(minRelevance)}, max {formatRelevance(maxRelevance)}
          </p>
        )}
      </div>

      {works.length === 0 ? (
        <div className="no-results">
          <p>{hasRelevanceFilter ? 'No results on this page match the current relevance filter.' : 'No results found for this page.'}</p>
        </div>
      ) : (
      <div className="results-list">
        {works.map((work, index) => (
          <div key={work.id || index} className="result-card">
            <div className="result-header-row">
              <span className="work-type-badge">{getWorkType(work.type)}</span>
              {work.open_access?.is_oa && (
                <span className="oa-badge">Open Access</span>
              )}
            </div>

            <h3 className="result-title">
              {work.doi ? (
                <a 
                  href={work.doi}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="title-link"
                >
                  {highlightText(work.display_name || work.title || 'Untitled')}
                  <FiExternalLink className="external-link-icon" />
                </a>
              ) : (
                <span className="title-link" style={{ cursor: 'default' }}>
                  {highlightText(work.display_name || work.title || 'Untitled')}
                </span>
              )}
            </h3>

            <div className="result-meta">
              {work.authorships && work.authorships.length > 0 && (
                <div className="meta-item">
                  <FiUser className="meta-icon" />
                  <span>{formatAuthors(work.authorships)}</span>
                </div>
              )}

              {work.publication_date && (
                <div className="meta-item">
                  <FiCalendar className="meta-icon" />
                  <span>{formatDate(work.publication_date)}</span>
                  {work.publication_year && (
                    <span className="year-badge">({work.publication_year})</span>
                  )}
                </div>
              )}

              {work.primary_location?.source && (
                <div className="meta-item">
                  <FiBookOpen className="meta-icon" />
                  <span>{removeOpenAlexUrls(work.primary_location.source.display_name || '')}</span>
                  {work.primary_location.source.host_organization && (
                    <span className="org-badge"> • {removeOpenAlexUrls(work.primary_location.source.host_organization)}</span>
                  )}
                </div>
              )}

              {work.cited_by_count !== undefined && work.cited_by_count !== null && (
                <div className="meta-item citation-count">
                  <FiTrendingUp className="meta-icon" />
                  <span>{formatCitationCount(work.cited_by_count)} citations</span>
                </div>
              )}
            </div>

            {(() => {
              // Handle both regular abstract and inverted index format
              let abstractText = work.abstract;
              if (!abstractText && work.abstract_inverted_index) {
                // Convert inverted index to text (OpenAlex format)
                // abstract_inverted_index is an object where keys are words and values are arrays of positions
                const inverted = work.abstract_inverted_index;
                const positions = [];
                
                // Collect all word positions
                for (const word in inverted) {
                  const wordPositions = inverted[word];
                  if (Array.isArray(wordPositions)) {
                    for (const pos of wordPositions) {
                      positions.push({ pos, word });
                    }
                  }
                }
                
                // Sort by position and join words
                positions.sort((a, b) => a.pos - b.pos);
                abstractText = positions.map(p => p.word).join(' ');
              }
              
              if (!abstractText || abstractText.trim() === '') return null;
              
              return (
                <div className="result-abstract-container">
                  <p 
                    className={`result-abstract ${expandedAbstracts.has(work.id) ? 'expanded' : ''}`}
                  >
                    {highlightText(
                      expandedAbstracts.has(work.id) || abstractText.length <= 300
                        ? abstractText
                        : `${abstractText.substring(0, 300)}...`
                    )}
                  </p>
                  {abstractText.length > 300 && (
                    <button
                      className="expand-abstract-button"
                      onClick={() => {
                        const newExpanded = new Set(expandedAbstracts);
                        if (newExpanded.has(work.id)) {
                          newExpanded.delete(work.id);
                        } else {
                          newExpanded.add(work.id);
                        }
                        setExpandedAbstracts(newExpanded);
                      }}
                    >
                      {expandedAbstracts.has(work.id) ? (
                        <>
                          <FiChevronUp /> Show Less
                        </>
                      ) : (
                        <>
                          <FiChevronDown /> Show More
                        </>
                      )}
                    </button>
                  )}
                </div>
              );
            })()}

            {work.concepts && work.concepts.length > 0 && (
              <div className="result-concepts">
                {work.concepts.slice(0, 5).map((concept, idx) => {
                  // Filter out any OpenAlex URLs from concept display
                  const conceptName = removeOpenAlexUrls(concept.display_name || '');
                  return conceptName ? (
                    <span key={idx} className="concept-tag">
                      {conceptName}
                    </span>
                  ) : null;
                })}
              </div>
            )}

            <div className="result-footer">
              <div className="result-footer-links">
                {work.doi && (
                  <a
                    href={work.doi}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="doi-link"
                  >
                    <FiLink /> DOI
                    <FiExternalLink />
                  </a>
                )}
              </div>
              <div className="result-footer-actions">
                {isCollectionView && collectionId && (
                  <div className="pdf-upload-section">
                    {pdfStatus[work.id]?.hasPdf ? (
                      <button
                        className="pdf-download-button"
                        onClick={() => handleDownloadPdf(work)}
                        title="Download PDF"
                      >
                        <FiDownload /> PDF
                      </button>
                    ) : (
                      <>
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={(e) => handleFileSelect(work, e)}
                          style={{ display: 'none' }}
                          id={`pdf-upload-${work.id}`}
                        />
                        <label
                          htmlFor={`pdf-upload-${work.id}`}
                          className={`pdf-upload-button ${pdfStatus[work.id]?.uploading ? 'uploading' : ''}`}
                          title="Upload PDF — stored securely on AWS infrastructure"
                        >
                          {pdfStatus[work.id]?.uploading ? (
                            <>⏳ Uploading...</>
                          ) : (
                            <>
                              <FiUpload /> Upload PDF
                            </>
                          )}
                        </label>
                      </>
                    )}
                  </div>
                )}
                {isCollectionView && onRemoveFromCollection && (
                  <button
                    className="remove-from-collection-button"
                    onClick={() => onRemoveFromCollection(work)}
                    title="Remove from collection"
                  >
                    <FiTrash2 /> Remove
                  </button>
                )}
                {isAuthenticated && onSaveToCollection && !isCollectionView && (
                  <button
                    className="save-to-collection-button"
                    onClick={() => onSaveToCollection(work)}
                    title="Save to collection"
                  >
                    <FiSave /> Save to Collection
                  </button>
                )}
                {!isAuthenticated && !isCollectionView && onSignInToSave && (
                  <button
                    className="save-to-collection-button"
                    onClick={onSignInToSave}
                    title="Sign in to save to a collection"
                    style={{ opacity: 0.75 }}
                  >
                    <FiSave /> Sign in to save
                  </button>
                )}
                <div className="citation-links">
                  <button
                    className="citation-link-button cites"
                    onClick={() => onCitationSearch && onCitationSearch(work, 'cites')}
                    title="Works cited by this work"
                  >
                    <FiArrowDown /> Cites
                  </button>
                  <button
                    className="citation-link-button cited-by"
                    onClick={() => onCitationSearch && onCitationSearch(work, 'cited_by')}
                    title="Works that cite this work"
                  >
                    <FiArrowUp /> Cited by
                  </button>
                  <button
                    className="citation-link-button related"
                    onClick={() => onCitationSearch && onCitationSearch(work, 'related')}
                    title="Related works"
                  >
                    <FiCornerDownRight /> Related
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-button"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
          >
            Previous
          </button>

          <div className="pagination-info">
            <span>Page {page} of {totalPages}</span>
          </div>

          <button
            className="pagination-button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchResults;

